import {
  activateEmailUser,
  activateThirdPartyUser,
  getUserInfo,
  listWorkspaces,
} from '@modusign/openapi-client-user';
import { GetUserResponseDto } from '@modusign/openapi-client-user/generated/model';
import { ActivateThirdPartyUserResponseDto } from '@modusign/openapi-client-user/model';
import { QueryClient } from 'react-query';
import {
  all,
  call,
  fork,
  getContext,
  put,
  retry,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { PromiseType } from 'utility-types';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { getSignInMutationKey } from '@app/features/authentication/hooks/useSignIn';
import { getThirdPartySignInMutationKey } from '@app/features/authentication/hooks/useThirdPartySignIn';
import {
  activateSubscription,
  changeSubscription,
  createSubscription,
  createSubscriptionSuccess,
  fetchSubscriptionData,
} from '@app/features/subscription/state';
import { login } from '@app/libs/login';
import workspaceService from '@app/libs/workspaceService';
import findActiveWorkspace from '@app/libs/workspaceService/findActiveWorkspace';
import {
  activateInactiveAccount,
  activateInactiveAccountFailure,
  activateInactiveAccountSuccess,
  activateThirdPartyInactiveAccount,
  activateThirdPartyInactiveAccountFailure,
  activateThirdPartyInactiveAccountSuccess,
  loadAccount,
  loadAccountFailure,
  loadAccountSuccess,
  setAuthentication,
} from '@app/store/account/actions';
import passwordSaga from '@app/store/account/password/sagas';
import signOutSaga from '@app/store/account/sign-out/sagas';
import signUpSaga from '@app/store/account/sign-up/sagas';
import surveySaga from '@app/store/account/survey/sagas';
import { thirdPartySignInSuccess } from '@app/store/account/third-party/actions';
import {
  ACCOUNT_INACTIVE_ACTIVATE_FAILURE,
  ACCOUNT_INACTIVE_ACTIVATE_REQUEST,
  ACCOUNT_INACTIVE_ACTIVATE_SUCCESS,
  ACCOUNT_LOAD_REQUEST,
  ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_FAILURE,
  ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_REQUEST,
  ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_SUCCESS,
} from '@app/store/actionTypes';
import { setUser, withAnalytics } from '@app/store/analytics/actions';
import { modalOpen } from '@app/store/modal/actions';
import { fetchUserAuthority } from '@app/store/user/authority/actions';

export function* loadAccountDataSaga() {
  try {
    const workspaces: PromiseType<ReturnType<typeof listWorkspaces>> = yield call(listWorkspaces);
    if (
      !workspaceService.hasWorkspaceId() ||
      !workspaces.workspaces.some((w) => w.id === workspaceService.getWorkspaceId())
    ) {
      const activeWorkspace = findActiveWorkspace(workspaces.workspaces);
      yield call(workspaceService.setWorkspaceId.bind(workspaceService), activeWorkspace.id);
    }
    yield put(fetchSubscriptionData());
    yield put(fetchUserAuthority());

    const userInfo: GetUserResponseDto = yield retry(3, 2 * 1000, getUserInfo);
    yield put(withAnalytics(setUser(userInfo.id), loadAccountSuccess(userInfo)));
  } catch (error) {
    yield put(loadAccountFailure(error));
  }
}

/** @deprecated */
export function* activateThirdPartyAccountSaga({
  payload: { code, provider },
}: ReturnType<typeof activateThirdPartyInactiveAccount>) {
  try {
    const {
      result: { accessToken, refreshToken },
    }: ActivateThirdPartyUserResponseDto = yield call(activateThirdPartyUser, { code, provider });
    yield call(login, { type: 'token', tokens: { accessToken, refreshToken } });
    yield put(loadAccount());
    yield put(setAuthentication(true));
    yield put(thirdPartySignInSuccess());
  } catch (error) {
    yield put(activateThirdPartyInactiveAccountFailure(error));
  }
}

/** @deprecated */
export function* activateAccountSaga({
  payload: { email, password },
}: ReturnType<typeof activateInactiveAccount>) {
  try {
    yield call(activateEmailUser, { email, password }, { credentials: false });
    yield put(activateInactiveAccountSuccess({ email, password }));
  } catch (error) {
    yield put(activateInactiveAccountFailure(error));
  }
}

/** @deprecated */
const ACCOUNT_ACTIVATE_RESPONSE: { [key: string]: string } = {
  'User is already activated': '이미 휴면 계정 해제가 완료된 유저입니다.',
};

/** @deprecated */
export function* notifyAccountActivateSaga(
  action:
    | ReturnType<typeof activateInactiveAccountSuccess>
    | ReturnType<typeof activateInactiveAccountFailure>
    | ReturnType<typeof activateThirdPartyInactiveAccountSuccess>
    | ReturnType<typeof activateThirdPartyInactiveAccountFailure>
) {
  switch (action.type) {
    case ACCOUNT_INACTIVE_ACTIVATE_FAILURE:
    case ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_FAILURE:
      yield put(
        modalOpen('CONFIRM', {
          content:
            ACCOUNT_ACTIVATE_RESPONSE[action.payload.message] ||
            GLOBAL_ERROR_MESSAGE[action.payload.message] ||
            action.payload.message,
        })
      );
      break;
    default:
      break;
  }
}

/** @deprecated */
export function* signInAfterActivateSaga({
  payload: { email, password },
}: ReturnType<typeof activateInactiveAccountSuccess>) {
  const client: QueryClient = yield getContext('queryClient');
  const signIn = client
    .getMutationCache()
    .build(client, { mutationKey: getSignInMutationKey(), variables: { email, password } });

  yield call(signIn.execute);
}

/** @deprecated */
export function* signInThirdPartyAfterActivateSaga({
  payload: { code, provider },
}: ReturnType<typeof activateThirdPartyInactiveAccountSuccess>) {
  const client: QueryClient = yield getContext('queryClient');
  const signIn = client.getMutationCache().build(client, {
    mutationKey: getThirdPartySignInMutationKey(),
    variables: { code, provider },
  });

  yield call(signIn.execute);
}

export default function* saga() {
  yield all([
    fork(passwordSaga),
    fork(signOutSaga),
    fork(signUpSaga),
    fork(surveySaga),
    takeLatest(
      [
        ACCOUNT_LOAD_REQUEST,
        activateSubscription.fulfilled.type,
        changeSubscription.fulfilled.type,
        createSubscription.fulfilled.type,
        createSubscriptionSuccess,
      ],
      loadAccountDataSaga
    ),
    takeLeading(ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_REQUEST, activateThirdPartyAccountSaga),
    takeLeading(ACCOUNT_INACTIVE_ACTIVATE_REQUEST, activateAccountSaga),
    takeEvery(
      [
        ACCOUNT_INACTIVE_ACTIVATE_SUCCESS,
        ACCOUNT_INACTIVE_ACTIVATE_FAILURE,
        ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_SUCCESS,
        ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_FAILURE,
      ],
      notifyAccountActivateSaga
    ),
    takeEvery(ACCOUNT_INACTIVE_ACTIVATE_SUCCESS, signInAfterActivateSaga),
    takeEvery(ACCOUNT_THIRD_PARTY_INACTIVE_ACTIVATE_SUCCESS, signInThirdPartyAfterActivateSaga),
  ]);
}
